import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, Edit, Trash2, Search, FileText, FileDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { brandSpecService } from '../services/api';
import { brandSpecsManager } from '../mock'; // For download and search functionality
import { brandSpecsManagerAPI } from '../services/apiManagers';
import { useToast } from '../hooks/use-toast';

const SimpleBrandSpecs = ({ searchQuery = '' }) => {
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const { toast } = useToast();
  // Temporary admin check - in production this would come from auth service
  const currentUser = { role: 'Admin' };
  const isAdmin = currentUser?.role === 'Admin';

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      const allBrands = await brandSpecService.getAllBrandSpecs();
      console.log('Loaded brands:', allBrands);
      setBrands(allBrands);
      setFilteredBrands(allBrands);
      
      // Sync with the manager for download and search functionality
      brandSpecsManager.brands = allBrands;
    } catch (error) {
      console.error('Error loading brands:', error);
      setBrands([]);
      setFilteredBrands([]);
      brandSpecsManager.brands = [];
    }
  };

  useEffect(() => {
    const query = searchQuery || localSearchQuery;
    if (query.trim() === '') {
      setFilteredBrands(brands);
    } else {
      // Update the manager's brands array for search functionality
      brandSpecsManagerAPI.brands = brands;
      const filtered = brandSpecsManagerAPI.searchBrands(query);
      setFilteredBrands(filtered);
    }
  }, [searchQuery, localSearchQuery, brands]);

  const handleDownloadAll = async (format = 'json') => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can download brand specifications.',
        variant: 'destructive'
      });
      return;
    }

    try {
      let result;
      if (format === 'pdf') {
        result = await brandSpecsManagerAPI.downloadAllBrandsPDF();
      } else {
        result = brandSpecsManagerAPI.downloadAllBrands();
      }
      
      if (result.success) {
        toast({
          title: 'Download Complete',
          description: result.message,
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: error.message || 'Failed to download brand specifications.',
        variant: 'destructive'
      });
    }
  };

  const handleDownloadBrand = async (brandId, brandName, format = 'json') => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can download brand specifications.',
        variant: 'destructive'
      });
      return;
    }

    try {
      let result;
      if (format === 'pdf') {
        result = await brandSpecsManagerAPI.downloadBrandPDF(brandId);
      } else {
        result = brandSpecsManagerAPI.downloadBrandById(brandId);
      }
      
      if (result.success) {
        toast({
          title: 'Download Complete',
          description: result.message,
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: error.message || 'Failed to download brand specification.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteBrand = async (brandId, brandName) => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can delete brand specifications.',
        variant: 'destructive'
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${brandName}? This Brand ID will never be reused.`)) {
      try {
        await brandSpecService.deleteBrandSpec(brandId);
        toast({
          title: 'Brand Deleted',
          description: `${brandName} (${brandId}) deleted successfully. This Brand ID will not be reused.`,
        });
        loadBrands(); // Refresh the list
      } catch (error) {
        console.error('Error deleting brand:', error);
        toast({
          title: 'Delete Failed',
          description: error.message || 'Failed to delete brand specification',
          variant: 'destructive'
        });
      }
    }
  };

  const handleEditBrand = (brandId) => {
    // Navigate to edit page (we'll create this)
    window.location.href = `/brand-specs/edit/${brandId}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Brand Specifications</h1>
          <div className="flex space-x-3">
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => handleDownloadAll('pdf')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download All PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDownloadAll('json')}
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Download All JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Link to="/brand-specs/add">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
                <span className="mr-2">+</span>
                Add Brand
              </button>
            </Link>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search brand names..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">BOPP Bag Specifications</h2>
            
            {(searchQuery || localSearchQuery) && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  {filteredBrands.length === 0 
                    ? `No brand specifications found for "${searchQuery || localSearchQuery}"`
                    : `Found ${filteredBrands.length} brand specification(s)`
                  }
                </p>
              </div>
            )}
            
            {filteredBrands.length === 0 ? (
              <p className="text-gray-600">
                {(searchQuery || localSearchQuery) ? `No brand specifications match your search.` : "No brand specifications found."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBrands.map((brand) => (
                      <tr key={brand.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <Link 
                              to={`/brand-specs/view/${brand.id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                            >
                              {brand.brandName}
                            </Link>
                            <div className="text-sm text-gray-500">{brand.brandCode}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {brand.buyerCompany}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {isAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditBrand(brand.id)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteBrand(brand.id, brand.brandName)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </>
                            )}
                            {isAdmin && (
                              <div className="relative inline-block">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => handleDownloadBrand(brand.id, brand.brandName, 'pdf')}
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      Download PDF
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleDownloadBrand(brand.id, brand.brandName, 'json')}
                                    >
                                      <FileDown className="w-4 h-4 mr-2" />
                                      Download JSON
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="mt-8">
          <div className="bg-white p-6 rounded-lg shadow max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900">Total Brands</h3>
            <p className="text-3xl font-bold text-blue-600">{brands.length}</p>
            {(searchQuery || localSearchQuery) && (
              <p className="text-sm text-gray-500 mt-2">
                {filteredBrands.length} matching results
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleBrandSpecs;